<template>
  <header
    class="site-nav position-sticky top-0"
    :class="{
      'site-nav--immersive': isImmersive,
      'site-nav--solid': showSolid,
    }"
  >
    <nav class="container-nm site-nav__container" aria-label="Main">
      <div
        class="site-nav__bar py-2"
        :class="{ 'site-nav__bar--open': menuOpen }"
      >
        <div class="site-nav__top">
          <div
            class="site-nav__brand-slot"
            :class="{ 'site-nav__brand-slot--visible': showBrand }"
            :aria-hidden="!showBrand"
          >
            <RouterLink
              to="/"
              class="site-nav__brand text-decoration-none"
              :tabindex="showBrand ? 0 : -1"
              @click="closeMenu"
            >
              <img src="/logo.svg" alt="NODEMOS" class="nav-logo" height="28" />
            </RouterLink>
          </div>

          <button
            class="site-nav__toggler d-lg-none"
            type="button"
            :aria-expanded="menuOpen"
            aria-controls="navMenu"
            aria-label="Toggle navigation"
            @click="toggleMenu"
          >
            <span class="site-nav__toggler-icon" aria-hidden="true" />
          </button>
        </div>

        <div
          id="navMenu"
          class="site-nav__menu"
          :class="{ 'site-nav__menu--open': menuOpen }"
        >
          <div class="site-nav__menu-inner">
            <div class="site-nav__items">
              <RouterLink
                v-for="link in header.links"
                :key="link.to"
                :to="link.to"
                class="nm-nav-link"
                @click="closeMenu"
              >
                {{ link.label }}
              </RouterLink>

              <div class="site-nav__actions">
                <RouterLink
                  v-for="action in header.actions"
                  :key="action.to"
                  :to="action.to"
                  class="nm-btn nm-btn--sm"
                  :class="action.variant === 'primary' ? 'nm-btn--primary' : 'nm-btn--ghost'"
                  @click="closeMenu"
                >
                  <i :class="action.icon" aria-hidden="true" />
                  {{ action.label }}
                </RouterLink>
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  </header>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { header } from '@/content/site'

const route = useRoute()
const menuOpen = ref(false)
const heroVisible = ref(false)
let heroObserver = null
let resizeObserver = null

const isHome = computed(() => route.name === 'home')
const isImmersive = computed(() => isHome.value && heroVisible.value && !menuOpen.value)
const showSolid = computed(() => !isImmersive.value)
const showBrand = computed(() => showSolid.value)

function toggleMenu() {
  menuOpen.value = !menuOpen.value
}

function closeMenu() {
  menuOpen.value = false
}

function navRootMargin() {
  const nav = document.querySelector('.site-nav')
  const height = nav?.getBoundingClientRect().height ?? 72
  return `-${Math.ceil(height)}px 0px 0px 0px`
}

function disconnectObserver() {
  heroObserver?.disconnect()
  heroObserver = null
}

function createObserver(sentinel) {
  disconnectObserver()

  heroObserver = new IntersectionObserver(
    ([entry]) => {
      heroVisible.value = entry.isIntersecting
    },
    { threshold: 0, rootMargin: navRootMargin() },
  )

  heroObserver.observe(sentinel)
}

function setupObserver() {
  disconnectObserver()
  heroVisible.value = false

  if (!isHome.value) return

  const sentinel = document.querySelector('[data-nav-hero-sentinel]')
  if (!sentinel) return

  createObserver(sentinel)
}

function refreshObserver() {
  if (!isHome.value) return

  const sentinel = document.querySelector('[data-nav-hero-sentinel]')
  if (!sentinel) return

  createObserver(sentinel)
}

watch(
  () => route.path,
  async () => {
    closeMenu()
    await nextTick()
    setupObserver()
  },
)

onMounted(() => {
  setupObserver()

  const nav = document.querySelector('.site-nav')
  if (nav && typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => {
      refreshObserver()
    })
    resizeObserver.observe(nav)
  }

  window.addEventListener('resize', refreshObserver, { passive: true })
})

onUnmounted(() => {
  disconnectObserver()
  resizeObserver?.disconnect()
  resizeObserver = null
  window.removeEventListener('resize', refreshObserver)
})
</script>

<style scoped>
.site-nav {
  z-index: 1020;
  overflow: visible;
}

.site-nav__container {
  padding-top: 0.85rem;
  padding-bottom: 0.85rem;
  transition: padding var(--nm-duration) var(--nm-ease-out);
}

.site-nav--solid .site-nav__container {
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
}

.site-nav__bar {
  border-radius: var(--nm-radius-lg);
  border: 1px solid transparent;
  background: transparent;
  box-shadow: none;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  transition:
    background var(--nm-duration) var(--nm-ease-out),
    border-color var(--nm-duration) var(--nm-ease-out),
    box-shadow var(--nm-duration) var(--nm-ease-out),
    backdrop-filter var(--nm-duration) var(--nm-ease-out);
}

.site-nav--solid .site-nav__bar,
.site-nav__bar--open {
  background: var(--nm-surface-glass);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  border-color: rgba(255, 255, 255, 0.08);
  box-shadow:
    var(--nm-raised-md),
    0 8px 32px rgba(21, 18, 40, 0.28);
}

.site-nav__top {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding-inline: clamp(0.75rem, 3vw, 1.25rem);
}

.site-nav__brand-slot {
  display: grid;
  grid-template-columns: 0fr;
  min-width: 0;
  flex: 1 1 auto;
  transition: grid-template-columns 0.32s var(--nm-ease-out);
}

.site-nav__brand-slot--visible {
  grid-template-columns: 1fr;
}

.site-nav__brand {
  display: flex;
  align-items: center;
  min-width: 0;
  overflow: hidden;
  background: transparent;
  opacity: 0;
  transform: translateX(-6px);
  transition:
    opacity 0.32s var(--nm-ease-out),
    transform 0.32s var(--nm-ease-out);
  pointer-events: none;
}

.site-nav__brand-slot--visible .site-nav__brand {
  opacity: 1;
  transform: translateX(0);
  pointer-events: auto;
}

.nav-logo {
  display: block;
  height: 1.75rem;
  width: auto;
  background: transparent;
}

.site-nav__toggler {
  flex: 0 0 auto;
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: var(--nm-radius-sm);
  background: var(--nm-surface);
  box-shadow: var(--nm-raised-sm);
  color: var(--nm-text-primary);
  transition:
    background var(--nm-duration) var(--nm-ease-out),
    box-shadow var(--nm-duration) var(--nm-ease-out),
    border-color var(--nm-duration) var(--nm-ease-out);
}

.site-nav__toggler:hover {
  background: var(--nm-surface-raised);
  box-shadow: var(--nm-raised-md);
}

.site-nav__toggler:focus-visible {
  outline: 2px solid var(--nm-accent-glow);
  outline-offset: 2px;
}

.site-nav__toggler-icon {
  display: block;
  width: 1.25rem;
  height: 1.25rem;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 30 30'%3e%3cpath stroke='rgba%28255, 255, 255, 0.82%29' stroke-linecap='round' stroke-miterlimit='10' stroke-width='2' d='M4 7h22M4 15h22M4 23h22'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: center;
  background-size: 100%;
}

.site-nav__menu-inner {
  padding-inline: clamp(0.75rem, 3vw, 1.25rem);
}

.site-nav__items {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.25rem;
}

.site-nav__actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-top: 0.5rem;
}

@media (min-width: 992px) {
  .site-nav__bar {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .site-nav__top {
    width: auto;
    flex: 0 0 auto;
    padding-inline: 0;
    padding-left: clamp(0.75rem, 3vw, 1.25rem);
  }

  .site-nav__brand-slot {
    flex: 0 0 auto;
  }

  .site-nav--immersive .site-nav__bar {
    justify-content: flex-end;
  }

  .site-nav__menu {
    display: flex;
    flex: 1 1 auto;
    justify-content: flex-end;
    padding-right: clamp(0.75rem, 3vw, 1.25rem);
  }

  .site-nav__menu-inner {
    padding-inline: 0;
    width: 100%;
  }

  .site-nav__items {
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    gap: 0.5rem;
    width: 100%;
  }

  .site-nav__actions {
    flex-direction: row;
    padding-top: 0;
    margin-left: 0.5rem;
  }
}

@media (max-width: 991.98px) {
  .site-nav__container,
  .site-nav--solid .site-nav__container {
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
  }

  .site-nav__bar {
    display: flex;
    flex-direction: column;
  }

  .site-nav__top {
    padding-inline: 0.65rem;
  }

  .site-nav__menu {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows 0.32s var(--nm-ease-out);
  }

  .site-nav__menu--open {
    grid-template-rows: 1fr;
  }

  .site-nav__menu-inner {
    min-height: 0;
    overflow: hidden;
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    padding-inline: 0.65rem;
    transition:
      opacity 0.24s var(--nm-ease-out),
      visibility 0.24s var(--nm-ease-out);
  }

  .site-nav__menu--open .site-nav__menu-inner {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    margin-top: 0.5rem;
    padding-top: 0.25rem;
    padding-bottom: 0.25rem;
  }

  .site-nav__items {
    padding-block: 0.75rem;
    gap: 0.25rem;
  }

  .site-nav__actions {
    width: 100%;
  }

  .site-nav__actions .nm-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>